// Login / Register page with Cloudflare Turnstile
import React, { useState, useEffect, useRef, useCallback } from "react";
import "../styles/Auth.css";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const API_URL =
  process.env.REACT_APP_API_URL !== undefined
    ? process.env.REACT_APP_API_URL
    : "http://localhost:3001";

export default function AuthPage({ onLogin, onRegister }) {
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Turnstile
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  const renderWidget = useCallback(() => {
    if (!TURNSTILE_SITE_KEY || !window.turnstile || !turnstileRef.current) return;

    // Remove existing widget if any
    if (widgetIdRef.current !== null) {
      try { window.turnstile.remove(widgetIdRef.current); } catch {}
      widgetIdRef.current = null;
    }

    widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      theme: "light",
      size: "flexible",
      callback: (token) => setTurnstileToken(token),
      "expired-callback": () => setTurnstileToken(""),
      "error-callback": () => setTurnstileToken(""),
    });
  }, []);

  useEffect(() => {
    // Wait for turnstile script to load
    if (window.turnstile) {
      renderWidget();
      return;
    }
    const interval = setInterval(() => {
      if (window.turnstile) {
        clearInterval(interval);
        renderWidget();
      }
    }, 200);
    return () => clearInterval(interval);
  }, [renderWidget]);

  // Re-render widget when switching login/register
  useEffect(() => {
    setTurnstileToken("");
    // Small delay to let DOM update
    const t = setTimeout(() => renderWidget(), 100);
    return () => clearTimeout(t);
  }, [isLogin, renderWidget]);

  const resetTurnstile = () => {
    setTurnstileToken("");
    if (widgetIdRef.current !== null && window.turnstile) {
      try { window.turnstile.reset(widgetIdRef.current); } catch {}
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError("Please complete the verification.");
      return;
    }

    setLoading(true);

    try {
      // Verify turnstile token server-side first
      if (TURNSTILE_SITE_KEY && turnstileToken) {
        const verifyRes = await fetch(`${API_URL}/api/auth/verify-turnstile`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: turnstileToken }),
        });
        const verifyData = await verifyRes.json();
        if (!verifyData.success) {
          setError("Verification failed. Please try again.");
          resetTurnstile();
          setLoading(false);
          return;
        }
      }

      if (isLogin) {
        await onLogin(email, password);
      } else {
        if (!name.trim()) {
          setError("Name is required.");
          setLoading(false);
          return;
        }
        await onRegister(email, password, name);
      }
    } catch (err) {
      setError(err.message || "Authentication failed.");
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-logo">LinkSquire</h1>
        <p className="auth-subtitle">
          {isLogin ? "Sign in to your account" : "Create a new account"}
        </p>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          {!isLogin && (
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required={!isLogin}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your Password"
              minLength={8}
              required
            />
          </div>

          {TURNSTILE_SITE_KEY && (
            <div className="turnstile-wrapper">
              <div ref={turnstileRef} />
            </div>
          )}

          <button type="submit" className="auth-btn" disabled={loading || (TURNSTILE_SITE_KEY && !turnstileToken)}>
            {loading ? <><span className="spinner-sm" /> Please wait...</> : isLogin ? "Sign In" : "Create Account"}
          </button>
        </form>

        <p className="auth-toggle">
          {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
          <button
            type="button"
            onClick={() => {
              setIsLogin(!isLogin);
              setError("");
            }}
          >
            {isLogin ? "Sign Up" : "Sign In"}
          </button>
        </p>
      </div>
    </div>
  );
}
